import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';
import { useI18n } from '../../../../i18n/I18nContext';

type LogRow = {
  _id: string;
  orderId: string;
  orderNumber: string;
  customerName?: string;
  customerPhone?: string;
  fromStatus?: string | null;
  toStatus: string;
  note?: string | null;
  actor?: string | null;
  createdAt: string;
};

const Logs = () => {
  const { state } = useContext(GlobalContext);
  const { t } = useI18n();
  const token = state?.auth?.token;

  const [logs, setLogs] = useState<LogRow[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = { page, limit };
    if (search.trim()) params.search = search.trim();
    return params;
  }, [page, limit, search]);

  const fetchLogs = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');

    try {
      const response = await axios.get('/orders/logs', {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams,
      });

      const list = Array.isArray(response?.data?.logs) ? response.data.logs : [];
      setLogs(list);
      setTotalPages(typeof response?.data?.totalPages === 'number' ? response.data.totalPages : 0);
    } catch (err: any) {
      setError(err?.response?.data?.message || t('logs_no_data'));
      setLogs([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [token, queryParams, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <Card className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">{t('logs_title')}</h4>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form
        className="d-flex gap-2 mb-3"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setSearch(searchInput);
        }}
      >
        <Form.Control
          placeholder={t('logs_search_placeholder')}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
        />
        <Button type="submit" variant="outline-dark">{t('common_search')}</Button>
        <Form.Select style={{ maxWidth: '110px' }} value={String(limit)} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }}>
          <option value="10">10</option>
          <option value="25">25</option>
          <option value="50">50</option>
        </Form.Select>
      </Form>

      {loading ? (
        <p>{t('common_loading')}</p>
      ) : (
        <Table striped bordered hover responsive size="sm">
          <thead>
            <tr>
              <th>{t('logs_time')}</th>
              <th>{t('logs_order')}</th>
              <th>{t('logs_customer')}</th>
              <th>{t('logs_transition')}</th>
              <th>{t('logs_actor')}</th>
              <th>{t('logs_note')}</th>
            </tr>
          </thead>
          <tbody>
            {logs.length > 0 ? (
              logs.map((log) => (
                <tr key={log._id}>
                  <td>{new Date(log.createdAt).toLocaleString()}</td>
                  <td>{log.orderNumber}</td>
                  <td>
                    <div>{log.customerName || t('common_na')}</div>
                    <small>{log.customerPhone || ''}</small>
                  </td>
                  <td>
                    <Badge bg="secondary" className="me-1">{log.fromStatus || t('logs_start')}</Badge>
                    <span className="me-1">{t('logs_to')}</span>
                    <Badge bg="dark">{log.toStatus}</Badge>
                  </td>
                  <td>{log.actor || t('logs_system')}</td>
                  <td>{log.note || '-'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center">{t('logs_no_data')}</td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <div className="d-flex justify-content-between align-items-center mt-2">
        <small>Page {page} of {Math.max(totalPages, 1)}</small>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>Prev</Button>
          <Button size="sm" variant="outline-secondary" disabled={totalPages === 0 || page >= totalPages} onClick={() => setPage((prev) => prev + 1)}>Next</Button>
        </div>
      </div>
    </Card>
  );
};

export default Logs;
